// src/components/MessengerSidebar.jsx
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Users, Search, ArrowLeft, Megaphone, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChatStore } from "../store/useChatStore";
import { useAuthStore } from "../store/useAuthStore";
import { getUserColor, getInitials } from "../utils/avatarColor";
import "./MessengerSidebar.css";

// Announcement ID (SVB only)
const ANNOUNCE_SVB_ID = "group:announcements:SVB";

// "10:30 AM" today, "Yesterday", else DD/MM/YY. Plain strings are shown as is.
function formatListTime(value) {
  if (!value) return "";
  const d = dayjs(value);
  if (!d.isValid()) return String(value);
  const now = dayjs();
  if (d.isSame(now, "day")) return d.format("hh:mm A");
  if (d.isSame(now.subtract(1, "day"), "day")) return "Yesterday";
  return d.format("DD/MM/YY");
}

export default function MessengerSidebar({
  onSelect,
  onBack,
  forceOpen = false,
}) {
  const {
    getUsers,
    users,
    selectedUser,
    setSelectedUser,
    isUsersLoading,
    unreadMap = {},
    clearUnreadCount,
    markUserAsRead,
    fetchAnnouncementsUnreadCounts,
    fetchAnnouncementUnreadCount,
  } = useChatStore();

  const onlineUsers = useAuthStore((s) => s.onlineUsers) || [];
  const authUser = useAuthStore((s) => s.authUser);

  const role = authUser?.role?.toLowerCase?.();
  const isAdmin = role === "admin";
  const isClient = role === "client";

  const [filter, setFilter] = useState("all"); // all | online | unread
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const onlineSet = useMemo(
    () => new Set(onlineUsers.map((id) => String(id?._id || id))),
    [onlineUsers],
  );

  /* -------------------- initial load -------------------- */
  useEffect(() => {
    getUsers({ silent: users.length > 0 });

    if (typeof fetchAnnouncementsUnreadCounts === "function") {
      fetchAnnouncementsUnreadCounts();
    } else if (typeof fetchAnnouncementUnreadCount === "function") {
      fetchAnnouncementUnreadCount("SVB");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------- refresh unread on focus -------------------- */
  useEffect(() => {
    const onFocus = () => {
      if (typeof fetchAnnouncementsUnreadCounts === "function") {
        fetchAnnouncementsUnreadCounts();
      } else if (typeof fetchAnnouncementUnreadCount === "function") {
        fetchAnnouncementUnreadCount("SVB");
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchAnnouncementsUnreadCounts, fetchAnnouncementUnreadCount]);

  /* -------------------- pinned threads -------------------- */
  const pinnedThreads = useMemo(() => {
    const svbUnread = Number(unreadMap[ANNOUNCE_SVB_ID] || 0);

    const svb = {
      _id: ANNOUNCE_SVB_ID,
      name: "Announcements — SVB",
      isGroup: true,
      lastMessage: "Company-wide updates for SVB",
      unreadCount: isAdmin ? 0 : svbUnread,
      companyKey: "SVB",
    };

    return [svb];
  }, [unreadMap, isAdmin]);

  /* -------------------- filter users -------------------- */
  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    let people = [...users];

    if (filter === "online") {
      people = people.filter((u) => onlineSet.has(String(u._id)));
    }

    if (filter === "unread") {
      people = people.filter(
        (u) => Number(unreadMap?.[u._id] ?? u.unreadCount ?? 0) > 0,
      );
    }

    if (term) {
      people = people.filter((u) =>
        (u.name || "").toLowerCase().includes(term),
      );
    }

    // Pinned announcements: hidden when a filter is active (online/unread)
    let basePinned = [];
    if (filter === "all") {
      const pinnedToInclude = pinnedThreads.filter((p) => {
        if (!term) return true;
        return (
          p.name.toLowerCase().includes(term) ||
          "announcements".includes(term) ||
          (p.companyKey || "").toLowerCase().includes(term)
        );
      });
      const pinnedIsSelected = pinnedThreads.some(
        (p) => selectedUser?._id === p._id,
      );
      basePinned = pinnedIsSelected ? pinnedThreads : pinnedToInclude;
    }

    return [...basePinned, ...people];
  }, [
    users,
    filter,
    onlineSet,
    searchTerm,
    pinnedThreads,
    selectedUser,
    unreadMap,
  ]);

  const isInitialLoading = isUsersLoading && users.length === 0;

  /* -------------------- helpers -------------------- */
  const handleBackClick = () => {
    if (typeof onBack === "function") onBack();
    else navigate("/admin");
  };

  // NOTE: messages are fetched by ChatContainer when selectedUser changes.
  // For announcements, getAnnouncements() marks them read FIRST and then
  // refreshes the count, so we must NOT fetch the count here (it raced with
  // the "mark read" request and brought the old badge back).
  const handleOpenThread = (u) => {
    setSelectedUser(u);

    if (typeof clearUnreadCount === "function") clearUnreadCount(u._id);
    if (typeof markUserAsRead === "function") markUserAsRead(u._id);

    onSelect && onSelect();
  };

  const emptyText = isClient
    ? "No contacts."
    : isAdmin
      ? "No employees found."
      : "No users found.";

  const sidebarClass = [
    "msb",
    selectedUser && !forceOpen ? "msb--chat-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  /* -------------------- RENDER -------------------- */
  return (
    <aside className={sidebarClass}>
      {/* TOP BAR */}
      <div className="msb__top">
        <button className="msb__back" onClick={handleBackClick}>
          <ArrowLeft size={16} />
          <span>Back to Dashboard</span>
        </button>
      </div>

      {/* TITLE */}
      <div className="msb__title">
        <Users size={18} />
        <span>Contacts</span>
      </div>

      {/* SEARCH + FILTERS */}
      <div className="msb__search-wrap">
        <div className="msb__search">
          <Search size={16} className="msb__search-icon" />
          <input
            type="text"
            placeholder="Search users…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="msb__search-clear"
              onClick={() => setSearchTerm("")}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="msb__chips">
          {[
            { key: "all", label: "All" },
            { key: "online", label: "Online" },
            { key: "unread", label: "Unread" },
          ].map((c) => (
            <button
              key={c.key}
              className={`msb__chip ${filter === c.key ? "is-active" : ""}`}
              onClick={() => setFilter(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* LIST */}
      <div className="msb__list">
        {isInitialLoading && <div className="msb__empty">Loading…</div>}

        {!isInitialLoading &&
          filteredUsers.map((user) => {
            const isGroup = Boolean(user.isGroup);
            const online = !isGroup && onlineSet.has(String(user._id));
            const active = selectedUser?._id === user._id;
            const unread = Number(
              unreadMap?.[user._id] ?? user.unreadCount ?? 0,
            );
            const time = isGroup ? "" : formatListTime(user.lastMessageTime);

            return (
              <button
                key={user._id}
                onClick={() => handleOpenThread(user)}
                className={[
                  "msb__row",
                  active ? "is-active" : "",
                  isGroup ? "is-pinned" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {/* Avatar + online dot */}
                <div className="msb__avatar-wrap">
                  <div
                    className="msb__avatar"
                    style={{
                      backgroundColor: isGroup
                        ? "#f59e0b"
                        : user.profilePic
                          ? "transparent"
                          : getUserColor(user._id),
                    }}
                  >
                    {isGroup ? (
                      <Megaphone size={20} />
                    ) : user.profilePic ? (
                      <img src={user.profilePic} alt={user.name} />
                    ) : (
                      <span>{getInitials(user.name)}</span>
                    )}
                  </div>
                  {online && <span className="msb__online-dot" />}
                </div>

                {/* Name + last message */}
                <div className="msb__info">
                  <div className="msb__info-top">
                    <span className="msb__name">{user.name}</span>
                    {time && (
                      <span
                        className={`msb__time ${unread > 0 ? "is-unread" : ""}`}
                      >
                        {time}
                      </span>
                    )}
                  </div>

                  <div className="msb__info-bottom">
                    <span
                      className={`msb__last ${unread > 0 ? "is-unread" : ""}`}
                    >
                      {user.lastMessage || "Start a conversation"}
                    </span>
                    {unread > 0 && (
                      <span className="msb__badge">
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}

        {!isInitialLoading && filteredUsers.length === 0 && (
          <div className="msb__empty">
            {filter === "online"
              ? "No one is online right now."
              : filter === "unread"
                ? "No unread chats."
                : emptyText}
          </div>
        )}
      </div>
    </aside>
  );
}